import ReactSwitch from 'react-switch';
import { useDispatch, useSelector } from 'react-redux';
import { getTitle, getVerseRange } from '@deaf-bible-society-2-0/deafbible-metadata-utils';

import styles from './styles.module.css';
import colors from '../../../../../styles/colors.module.css';
import { RootState } from '../../../../../store';
import {
  selectedBBBVideo,
  selectedChapterVideos,
} from '../../../../../store/translation/selectors';
import { setShowChapterSegments } from '../../../../../store/settings';
import useURLManipulator from '../../../../../hooks/useURLManipulator';

/**
 * Lists the active chapter's video segments (verse range + title) below the
 * chapter-number grid in the BBB sidebar, with an inline switch to show or hide
 * the list. The show/hide choice is persisted in settings.
 */
export const ChapterSegments = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { selectBBBVideo } = useURLManipulator();

  const videos = useSelector(selectedChapterVideos);
  const currentVideo = useSelector(selectedBBBVideo);
  const chapter = useSelector((state: RootState) => state.params.chapter) ?? '';
  const { darkMode, showChapterSegments } = useSelector((state: RootState) => state.settings);

  if (videos.length === 0) {
    return null;
  }

  const toggle = () => dispatch(setShowChapterSegments(!showChapterSegments));

  const container = `${styles.container} ${darkMode && styles.containerDarkMode}`;
  const label = `${styles.label} ${darkMode && styles.labelDarkMode}`;

  const ranges = videos.map(video => `(${getVerseRange(video, chapter)})`);
  const rangeWidth = `${Math.max(...ranges.map(range => range.length))}ch`;

  return (
    <div className={container}>
      <div className={styles.header}>
        <div className={label}>Verse Parts</div>
        <ReactSwitch
          checked={showChapterSegments}
          onChange={toggle}
          checkedIcon={false}
          uncheckedIcon={false}
          height={20}
          width={40}
          handleDiameter={15}
          offColor={colors.gray1}
          onColor={colors.cinnabar}
        />
      </div>

      {showChapterSegments && (
        <div className={styles.list}>
          {videos.map(video => {
            const isSelected = video.bbb_id === currentVideo?.bbb_id;
            const range = getVerseRange(video, chapter);
            const title = getTitle(video);
            const segmentClass = `${styles.segment} ${darkMode && styles.segmentDarkMode} ${
              isSelected && styles.segmentSelected
            }`;

            return (
              <button
                key={video.bbb_id}
                type="button"
                className={segmentClass}
                onClick={() => selectBBBVideo(video)}
              >
                <span className={styles.range} style={{ minWidth: rangeWidth }}>
                  {`(${range})`}
                </span>
                <span className={styles.title}>{title}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
